import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getAllEvents } from '../lib/api-client';
import type { Event } from '../types';

export const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await getAllEvents();
        setEvents(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch events:', err);
        setError('Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Search Section */}
      <Box
        sx={{
          pt: '52px', // Account for status bar
          px: 2,
          pb: 2,
        }}
      >
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search Events"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            sx: {
              bgcolor: '#f5f5f5',
              borderRadius: '12px',
              height: '64px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& input': {
                fontSize: '16px',
                fontWeight: 500,
              },
            },
          }}
          sx={{
            mb: 2,
          }}
        />

        {/* Filter and Sort Buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              textTransform: 'none',
              color: 'rgba(0, 0, 0, 0.9)',
              borderColor: '#e6e6e6',
              fontSize: '14px',
              fontWeight: 500,
              px: '10px',
              py: '6px',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#e6e6e6',
                bgcolor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              textTransform: 'none',
              color: '#1a1a1a',
              borderColor: '#e6e6e6',
              fontSize: '14px',
              fontWeight: 500,
              px: '10px',
              py: '6px',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#e6e6e6',
                bgcolor: 'rgba(0, 0, 0, 0.02)',
              },
            }}
          >
            Sort
          </Button>
        </Box>
      </Box>

      {/* Events List */}
      <Box
        sx={{
          flex: 1,
          px: 2,
          pb: '90px', // Account for bottom tab bar
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Typography color="textSecondary">No events found</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {events.map((event) => (
              <Card
                key={event.id}
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '1px solid #e6e6e6',
                  borderRadius: '12px',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => navigate(`/events/${event.id}`)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: '18px' }}>
                        {event.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 0.5, fontSize: '14px' }}
                      >
                        {new Date(event.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
                        📍 {event.location}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {event.isActive ? 'Active' : 'Inactive'}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
